import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Network {
  id: string;
  networkName: string;
  description: string;
  
  // Campi operativi
  assignedCompanies: string | null;
  synergiesInternationalPaintlessDentRepairCompaniesCount: number | null;
  synergiesNationalPaintlessDentRepairCompaniesCount: number | null;
  
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface NetworkBodyShop {
  id: string;
  networkId?: string;
  networkName?: string;
  // Nominativo: legal entity
  businessName?: string;
  // Nominativo: physical person
  firstname?: string;
  lastname?: string;
  vatNumber?: string;
  status: string;
  solCode?: string;
  legalAddress?: {
    streetType?: string;
    streetName?: string;
    streetNumber?: string | number;
    municipality?: string;
    province?: string;
    zipCode?: string;
  };
  facilityAddress?: {
    streetType?: string;
    streetName?: string;
    streetNumber?: string | number;
    municipality?: string;
    province?: string;
    zipCode?: string;
  };
  isNetworkHead?: boolean;
  networkProgressiveNumber?: number;
  addedToNetworkAt: string;
  specificDataSectionStatus?: string;
  cessationDate?: string | null;
  // Computed client-side during data enrichment
  nominativo?: string;
  indirizzoCompatto?: string;
}

export interface NetworkContract {
  contractId: string;
  networkId: string;
  serviceType: string;
  startDate: string;
  endDate: string;
  contractStatus: string;
  buyerName: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

export interface AddNetworkBodyShopRequest {
  businessName: string;
  vatNumber?: string;
  bodyShopType?: string;
  controllante?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.professionalsApiUrl}/api/v1/networks`;

  /**
   * Ottenere un Network
   * @param professionalId ID del network
   */
  getNetwork(professionalId: string): Observable<ApiResponse<Network>> {
    return this.http.get<ApiResponse<Network>>(`${this.baseUrl}/${professionalId}`);
  }

  /**
   * Ottenere tutte le Body Shop di un Network
   * @param networkId ID del network
   */
  getNetworkBodyShops(networkId: string): Observable<ApiResponse<NetworkBodyShop[]>> {
    return this.http.get<ApiResponse<NetworkBodyShop[]>>(`${this.baseUrl}/${networkId}/body-shops`);
  }

  /**
   * Ottenere la Controllante di un Network
   * @param networkId ID del network
   */
  getControllante(networkId: string): Observable<ApiResponse<NetworkBodyShop>> {
    return this.http.get<ApiResponse<NetworkBodyShop>>(`${this.baseUrl}/${networkId}/controllante`);
  }

  /**
   * Aggiungere una Body Shop a un Network (crea un nuovo professionista NET-XXXXX)
   * @param networkId ID del network
   * @param request Dati della nuova body shop
   */
  addBodyShopToNetwork(
    networkId: string,
    request: AddNetworkBodyShopRequest
  ): Observable<ApiResponse<NetworkBodyShop>> {
    return this.http.post<ApiResponse<NetworkBodyShop>>(
      `${this.baseUrl}/${networkId}/body-shops`,
      request
    );
  }

  /**
   * Rimuovere una Body Shop da un Network
   * @param bodyShopId ID della Body Shop da rimuovere
   */
  removeBodyShopFromNetwork(bodyShopId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/body-shops/${bodyShopId}`);
  }

  /**
   * Aggiornare lo status di una Body Shop (sospendi / riattiva)
   * @param bodyShopId ID della Body Shop
   * @param action 'SUSPEND' | 'REACTIVATE'
   */
  updateBodyShopStatus(bodyShopId: string, action: 'SUSPEND' | 'REACTIVATE'): Observable<ApiResponse<NetworkBodyShop>> {
    return this.http.patch<ApiResponse<NetworkBodyShop>>(
      `${this.baseUrl}/body-shops/${bodyShopId}/status`,
      null,
      { params: { action } }
    );
  }

  /**
   * Ottenere tutti i contratti di un Network
   * @param networkId ID del network
   */
  getNetworkContracts(networkId: string): Observable<ApiResponse<NetworkContract[]>> {
    return this.http.get<ApiResponse<NetworkContract[]>>(`${this.baseUrl}/${networkId}/contracts`);
  }
}
